import { BottomSheet } from '@s4l/ds'
import HtmlViewer from 'client/components/HtmlViewer'
import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonLink from 'src/comps/Atom/Buttons/ButtonLink'
import { Text } from 'src/comps/Atom/Text'
import ShowMore from 'src/comps/ShowMore'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { usePage } from 'src/hooks/usePage'
import {
  CheckinConditions,
  CheckoutConditions,
  FeesConditions,
  PoliciesConditions,
  PropertyConditions,
} from 'src/types/property'
import { countBlocksWithContent } from './helpers'
import messages from './messages'
import { ConditionsBase, SubTitle, TextStyle, Title } from './styles'
import styles from './styles.module.css'

const Checkin = ({ beginTime, endTime, minAge = 0 }: CheckinConditions) => {
  if (!beginTime && !endTime) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.checkIn} />
      </SubTitle>
      <TextStyle>
        <FormattedMessage {...messages.checkInTime} />
        {` ${beginTime} - ${endTime} `}
        {!!minAge && (
          <p>
            <FormattedMessage {...messages.minAge} />
            {`: ${minAge}`}
          </p>
        )}
      </TextStyle>
    </>
  )
}

const Checkout = ({ time }: CheckoutConditions) => {
  if (!time) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.checkOut} />
      </SubTitle>
      <TextStyle>
        <FormattedMessage {...messages.checkOutTime} />
        {` ${time}`}
      </TextStyle>
    </>
  )
}

const Instructions = ({ instructions }: CheckinConditions) => {
  if (!instructions) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.instructions} />
      </SubTitle>
      <TextStyle>
        <HtmlViewer html={instructions} />
      </TextStyle>
    </>
  )
}

const SpecialInstructions = ({ specialInstructions }: CheckinConditions) => {
  if (!specialInstructions) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.specialInstructions} />
      </SubTitle>
      <TextStyle>
        <HtmlViewer html={specialInstructions} />
      </TextStyle>
    </>
  )
}

const Fees = ({ mandatory, optional }: FeesConditions) => {
  if (!mandatory && !optional) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.fees} />
      </SubTitle>
      <TextStyle data-testid="fees">
        {mandatory && <HtmlViewer html={mandatory} />}
        {optional && <HtmlViewer html={optional} />}
      </TextStyle>
    </>
  )
}

const Policies = ({ knowBeforeYouGo }: PoliciesConditions) => {
  if (!knowBeforeYouGo) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.policies} />
      </SubTitle>
      <TextStyle>
        <HtmlViewer html={knowBeforeYouGo} />
      </TextStyle>
    </>
  )
}

const AdditionalComments = ({ comments }: { comments?: string }) => {
  if (!comments || !comments.length) {
    return <></>
  }

  return (
    <>
      <SubTitle>
        <FormattedMessage {...messages.additionalInformation} />
      </SubTitle>
      <TextStyle>
        <HtmlViewer html={comments} />
      </TextStyle>
    </>
  )
}

const Conditions = (props: PropertyConditions & { onModal: boolean }) => {
  const { checkin, checkout, fees, policies, comments, onModal } = props
  const conditionsWithContent = useMemo(
    () => countBlocksWithContent(props as unknown as Record<string, object>),
    [props]
  )

  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const checkInRef = useRef<HTMLDivElement>(null)
  const instructionsRef = useRef<HTMLDivElement>(null)
  const feesRef = useRef<HTMLDivElement>(null)
  const policiesRef = useRef<HTMLDivElement>(null)
  const { pageId } = usePage()

  const { sendEvent } = useKafkaEvent({ page: pageId, eventName: 'open_important_information' })

  const refs = {
    checkInRef,
    instructionsRef,
    feesRef,
    policiesRef,
  }

  if (!conditionsWithContent) {
    return <></>
  }

  const openDescription = (key: keyof typeof refs) => {
    setIsOpen(true)
    requestAnimationFrame(() => {
      refs[key].current?.scrollIntoView({ block: 'end' })
    })

    sendEvent({ section: SECTIONS.HOTEL_IMPORTANT_INFO }, { item: key })
  }

  if (onModal) {
    return (
      <section className={styles.comments}>
        <div
          role="button"
          tabIndex={0}
          onKeyUp={() => openDescription('checkInRef')}
          onClick={() => openDescription('checkInRef')}
        >
          {(checkin.beginTime || checkin.endTime || checkout.time) && (
            <dl className={styles.checkinContainer}>
              <div>
                <Text as="dt" variant="textBodyM">
                  {intl.formatMessage({ id: 'search.checkin' })}
                </Text>
                <Text as="dd" variant="textBodyL" weight="weight3">
                  {` ${checkin.beginTime} - ${checkin.endTime} `}
                </Text>
              </div>
              <div className={styles.lineContainer}>
                <div className={styles.circle} />
                <svg
                  width="100%"
                  height="2px"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <line x1="0" y1="1" x2="100%" y2="1" strokeWidth="2" strokeDasharray="4 4" />
                </svg>
                <div className={styles.circle} />
              </div>
              <div className={styles.alignEnd}>
                <Text as="dt" variant="textBodyM">
                  {intl.formatMessage({ id: 'search.checkout' })}
                </Text>
                <Text as="dd" variant="textBodyL" weight="weight3">
                  {checkout.time}
                </Text>
              </div>
            </dl>
          )}
        </div>
        <div className={styles.linkWrapper}>
          {(checkin.instructions || checkin.specialInstructions) && (
            <ButtonLink
              onClick={() => openDescription('instructionsRef')}
              text="global.instructions"
            />
          )}
          {(fees.mandatory || fees.optional) && (
            <ButtonLink onClick={() => openDescription('feesRef')} text="global.fees" />
          )}
          {policies.knowBeforeYouGo && (
            <ButtonLink onClick={() => openDescription('policiesRef')} text="global.policies" />
          )}
        </div>
        <BottomSheet open={isOpen} onClose={() => setIsOpen(false)}>
          <BottomSheet.Content
            title={intl.formatMessage({ id: 'global.checkin-important-information' })}
            modalOnDesktop
            scroll
          >
            {checkin.beginTime && checkin.endTime ? (
              <div className={styles.textWrapper} ref={checkInRef}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'search.checkin' })}
                </Text>

                <Text as="p" variant="textBodyL">
                  {intl.formatMessage({ id: 'global.checkin-time' })}
                  {` ${checkin.beginTime} - ${checkin.endTime} `}
                </Text>
                {!!checkin.minAge && (
                  <Text as="p" variant="textBodyL">
                    {intl.formatMessage({ id: 'global.minimum-checkin-age' })}
                    {`: ${checkin.minAge}`}
                  </Text>
                )}
              </div>
            ) : null}

            {checkout.time ? (
              <div className={styles.textWrapper}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'search.checkout' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  {intl.formatMessage({ id: 'global.checkout-time' })}
                  {` ${checkout.time}`}
                </Text>
              </div>
            ) : null}
            {checkin.instructions ? (
              <div className={styles.textWrapper} ref={instructionsRef}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'global.instructions' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  <HtmlViewer html={checkin.instructions} />
                </Text>
              </div>
            ) : null}
            {checkin.specialInstructions ? (
              <div className={styles.textWrapper}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'global.special-instructions' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  <HtmlViewer html={checkin.specialInstructions} />
                </Text>
              </div>
            ) : null}
            {fees.mandatory || fees.optional ? (
              <div className={styles.textWrapper} ref={feesRef}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'global.fees' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  <HtmlViewer html={fees?.mandatory ?? ''} />
                  <HtmlViewer html={fees?.optional ?? ''} />
                </Text>
              </div>
            ) : null}
            {policies.knowBeforeYouGo ? (
              <div className={styles.textWrapper} ref={policiesRef}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'global.policies' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  <HtmlViewer html={policies.knowBeforeYouGo} />
                </Text>
              </div>
            ) : null}
            {comments ? (
              <div className={styles.textWrapper}>
                <Text as="h3" variant="textBodyL" weight="weight3">
                  {intl.formatMessage({ id: 'global.additional-information' })}
                </Text>
                <Text as="p" variant="textBodyL">
                  <HtmlViewer html={comments} />
                </Text>
              </div>
            ) : null}
          </BottomSheet.Content>
        </BottomSheet>
      </section>
    )
  }

  return (
    <ConditionsBase>
      <Title>
        <FormattedMessage {...messages.title} />
      </Title>
      <ShowMore blocksToShow={3}>
        {checkin.beginTime && checkin.endTime ? (
          <Checkin
            beginTime={checkin.beginTime}
            endTime={checkin.endTime}
            minAge={checkin?.minAge}
          />
        ) : null}
        {checkout.time ? <Checkout time={checkout.time} /> : null}
        {checkin.instructions ? <Instructions instructions={checkin.instructions} /> : null}
        {checkin.specialInstructions ? (
          <SpecialInstructions specialInstructions={checkin.specialInstructions} />
        ) : null}
        {fees.mandatory || fees.optional ? (
          <Fees mandatory={fees.mandatory} optional={fees.optional} />
        ) : null}
        {policies.knowBeforeYouGo ? <Policies knowBeforeYouGo={policies.knowBeforeYouGo} /> : null}
        {comments ? <AdditionalComments comments={comments} /> : null}
      </ShowMore>
    </ConditionsBase>
  )
}

export default Conditions
